// Store form instances in a WeakMap to allow garbage collection when forms are removed.
// Each form element will have its own state object.
const formInstances = new WeakMap();

const unconditionalDefaultsEntities = [
	'Forms\\Entities\\ActionFieldVisibility',
	'Forms\\Entities\\ActionGroupVisibility',
	'Forms\\Entities\\ActionStepVisibility',
];

const showStateDefaultsEntities = ['Forms\\Entities\\ActionFieldVisibility', 'Forms\\Entities\\ActionGroupVisibility'];

const delayedInputTypes = ['text', 'number', 'date', 'datetime', 'time'];

/**
 * Retrieves or creates a data object associated with a given form.
 * The data object stores state information like default display styles,
 * unconditional defaults, removed required fields, original field values,
 * added required fields, and class changes.
 *
 * @param {HTMLElement} form - The form element.
 * @returns {Object} The data object associated with the form.
 */
function getFormData(form) {
	// If the form does not have an associated data object, create one
	if (!formInstances.has(form)) {
		formInstances.set(form, {
			// Map of original computed display styles for elements
			defaultDisplayStates: new Map(),
			// Map of unconditional default states set by conditionals
			unconditionalDefaults: new Map(),
			// Map tracking fields that had their 'required' attribute removed
			removedRequiredFields: new Map(),
			// Map tracking original values of fields before any modifications
			originalFieldValues: new Map(),
			// Map tracking fields where 'required' was added by actions
			addedRequiredFields: new Map(),
			// Map tracking any changes to classes on elements
			classChanges: new Map(),
			// Map of conditionals' actions with 'show' state fields
			conditionalShowFields: new Set(),
		});
	}
	// Return the stored data for this form
	return formInstances.get(form);
}

/**
 * Delay a function execution by specific time
 *
 * @param func
 * @param delay
 *
 * @returns {(function(...[*]): void)|*}
 */
function debounce(func, delay) {
	let timeoutId;

	return function (...args) {
		clearTimeout(timeoutId);

		timeoutId = setTimeout(() => {
			func.apply(this, args);
		}, delay);
	};
}

/**
 * Resets a form field to its default (empty) state.
 * For checkboxes and radio buttons, it unchecks them.
 * For all other input types, it sets the value to an empty string.
 *
 * @param {HTMLElement} field - The form field element.
 */
function resetField(field) {
	if (field.type === 'checkbox' || field.type === 'radio') {
		// Uncheck checkboxes and radio buttons
		field.checked = false;
		return;
	}
	// Clear value for other input types
	field.value = '';
}

/**
 * Retrieves the target element for a field visibility action.
 * Otherwise, select the '.base-element-block' containing the field with the given id.
 *
 * @param {HTMLElement} form - The form element to search within.
 * @param {string} elementId - The id of the field from action.settings.field.
 * @returns {HTMLElement|null} The target element or null if not found.
 */
function getTargetElement(form, elementId) {
	const field = form.querySelector(`[data-id="${elementId}"]`);

	if (field) {
		return field.closest('.base-element-block') || field.closest('.static-element-block') || field;
	}

	return null;
}

/**
 * Retrieves the field elements for actions that operate on fields.
 * Returns all fields with the given id.
 *
 * @param {HTMLElement} form - The form element.
 * @param {string} elementId - The id of the field.
 * @returns {HTMLElement[]} An array of field elements.
 */
function getFieldElements(form, elementId) {
	return Array.from(form.querySelectorAll(`[data-id="${elementId}"]`));
}

/**
 * Initialize form's steps
 *
 * @param form
 * @param formData
 */
function initFormsSteps(form, formData) {
	form.querySelectorAll('.form-step').forEach((step) => {
		// Get the step's unique name from its data attribute.
		const stepName = step.getAttribute('data-step-name');

		if (!stepName) {
			return;
		}

		// Save the current inline display style and class names
		const originalDisplay = step.style.display;
		const originalClasses = step.className;

		// Temporarily remove the 'js-hidden' class to ensure the element is visible
		step.classList.remove('js-hidden');

		// Clear inline display style to get the computed style
		step.style.display = '';

		// Get the computed display style from the browser
		const computed = getComputedStyle(step).display;

		// Restore the original classes and inline display
		step.className = originalClasses;
		step.style.display = originalDisplay;

		// Store the computed display state for later restoration
		formData.defaultDisplayStates.set(stepName, computed);
	});
}

/**
 * Initialize conditionals' field visibility
 *
 * @param conditionals
 * @param form
 * @param formData
 */
function conditionalsFieldVisibility(conditionals, form, formData) {
	const allConditionalFields = new Set();

	conditionals.forEach((conditional) => {
		conditional.actions.forEach((action) => {
			// If the action affects field visibility, add its target field name
			if (action.entity === 'Forms\\Entities\\ActionFieldVisibility') {
				allConditionalFields.add(action.settings.field);
			}
		});
	});

	// For each targeted field, compute and store its default container display style
	allConditionalFields.forEach((elementId) => {
		const targetElement = getTargetElement(form, elementId);

		if (!targetElement) {
			return;
		}

		const computed = getComputedStyle(targetElement).display;
		formData.defaultDisplayStates.set(elementId, computed);
	});
}

/**
 * Initialize field's event listeners
 *
 * @param conditionals
 * @param form
 * @param formData
 */
function initFieldsEventListeners(conditionals, form, formData) {
	const trackedFields = new Set();

	conditionals.forEach((cond) => {
		cond.conditions.forEach((condition) => {
			// Add the field name used in this condition to the tracked set
			trackedFields.add(condition.settings.field);
		});
	});

	trackedFields.forEach((elementId) => {
		// Select all inputs with the given element id
		const inputs = getFieldElements(form, elementId);

		inputs.forEach((input) => {
			const handler = delayedInputTypes.includes(input.type)
				? debounce(() => evaluateConditions(form, conditionals, formData), 1000)
				: () => evaluateConditions(form, conditionals, formData);

			input.addEventListener('input', handler);
		});
	});
}

/**
 * Retrieves and store default groups display styles
 *
 * @param form
 * @param formData
 */
function storeDefaultGroupsDisplayStates(form, formData) {
	form.querySelectorAll('fieldset.element-group-block').forEach((group) => {
		const groupId = group.getAttribute('data-id');

		if (!groupId) {
			return;
		}

		// Get and store the computed display style of the group
		const computed = getComputedStyle(group).display;
		formData.defaultDisplayStates.set(groupId, computed);
	});
}

/**
 * Retrieves all fields and store its original values
 *
 * @param form
 * @param formData
 */
function storeAllInputFieldsStates(form, formData) {
	form.querySelectorAll('input, select, textarea').forEach((field) => {
		formData.originalFieldValues.set(field.name, field.value);
	});
}

/**
 * Check all conditionals' actions and store "show" state fields
 *
 * @param conditionals
 * @param formData
 */
function storeConditionalShowFields(conditionals, formData) {
	conditionals.forEach((conditional) => {
		if (conditional.conditions.length === 0) {
			return;
		}

		conditional.actions.forEach((action) => {
			if (showStateDefaultsEntities.includes(action.entity) && action.settings.state === 'show') {
				formData.conditionalShowFields.add(action.settings.field);
			}
		});
	});
}

// Wait for the DOM content to load before initializing form behavior
document.addEventListener('DOMContentLoaded', function () {
	// Process each form with the class 'coredna-js-form'
	document.querySelectorAll('.coredna-js-form').forEach((form) => {
		// Extract the form ID
		const formId = form.querySelector('input[name="_formId"]').value;

		// Get the form-specific conditionals
		const conditionals = window['formConditionals' + formId];

		// Check if conditionals exist for this form
		if (!conditionals) {
			console.error(`Conditionals not found for form ID: ${formId}`);
			return;
		}

		// Retrieve the state data for this form
		const formData = getFormData(form);

		// Store conditionals' actions which uses 'show' state
		storeConditionalShowFields(conditionals, formData);

		// For each step in the form, compute and store its default display state
		initFormsSteps(form, formData);

		// Identify all fields that are targeted by conditionals for field visibility
		conditionalsFieldVisibility(conditionals, form, formData);

		// Attach input event listeners to all fields that are used in conditional logic
		initFieldsEventListeners(conditionals, form, formData);

		// For each group fieldset, store its default computed display state
		storeDefaultGroupsDisplayStates(form, formData);

		// Store the original value of every input, select, and textarea for potential restoration
		storeAllInputFieldsStates(form, formData);

		// This sets up the initial state of the form based on conditionals
		evaluateConditions(form, conditionals, formData);
	});
});

/**
 * Process unconditional conditionals (those with no conditions).
 * These are applied once and set default states.
 *
 * @param conditionals
 * @param form
 * @param formData
 */
function processUnconditionalConditionals(conditionals, form, formData) {
	conditionals.forEach((conditional) => {
		if (conditional.conditions.length > 0) {
			return;
		}

		// Only apply if not already applied
		if (conditional._defaultApplied === true) {
			return;
		}

		conditional.actions.forEach((action) => {
			applyAction(form, action);

			// Record the default state for visibility actions
			if (unconditionalDefaultsEntities.includes(action.entity)) {
				formData.unconditionalDefaults.set(action.settings.field, action.settings.state);
			}
		});

		conditional._defaultApplied = true;
	});
}

/**
 * Determine final visibility state for each target field.
 * Start with the unconditional default state, then override with triggered actions.
 * Priority: "hide" takes precedence over "show".
 *
 * @param formData
 * @param triggeredActions
 * @param form
 */
function determineFinalVisibility(formData, triggeredActions, form) {
	const allTargets = new Set([...formData.defaultDisplayStates.keys()]);

	allTargets.forEach((fieldName) => {
		let initialState = 'show';

		if (formData.unconditionalDefaults.has(fieldName)) {
			initialState = formData.unconditionalDefaults.get(fieldName);
		} else if (formData.conditionalShowFields.has(fieldName)) {
			initialState = 'hide';
		}

		let finalState = initialState;

		if (triggeredActions[fieldName]) {
			if (triggeredActions[fieldName].includes('hide')) {
				finalState = 'hide';
			} else if (triggeredActions[fieldName].includes('show')) {
				finalState = 'show';
			}
		}

		// Find the target element
		const targetElement = getTargetElement(form, fieldName);

		if (!targetElement) {
			return;
		}

		if (finalState === 'hide') {
			targetElement.style.display = 'none';

			// Remove 'required' from all fields within the target element
			targetElement.querySelectorAll('input, select, textarea').forEach((input) => {
				const key = input.id || input.name;

				if (input.hasAttribute('required')) {
					formData.removedRequiredFields.set(key, {
						element: input,
						wasRequired: true,
						source: 'field_visibility',
					});

					input.removeAttribute('required');
				}

				resetField(input);
			});

			return;
		}

		targetElement.style.display = formData.defaultDisplayStates.get(fieldName) || '';

		// Restore 'required' for all fields within the target element
		targetElement.querySelectorAll('input, select, textarea').forEach((input) => {
			const key = input.id || input.name;

			if (
				formData.removedRequiredFields.has(key) &&
				formData.removedRequiredFields.get(key).source === 'field_visibility'
			) {
				const record = formData.removedRequiredFields.get(key);

				if (record.wasRequired) {
					input.setAttribute('required', '');
				}
			}
		});
	});
}

/**
 * Evaluate each condition.
 * Returns true if all conditions are met, false if one fails.
 *
 * @param conditional
 * @param form
 * @returns {this is T[]}
 */
function processConditions(conditional, form) {
	return conditional.conditions.every((condition) => {
		// Get the field elements for this condition
		const fields = getFieldElements(form, condition.settings.field);

		if (fields.length === 0) {
			return false;
		}

		// Evaluate condition based on its entity type
		switch (condition.entity) {
			case 'Forms\\Entities\\ConditionString':
				return evaluateConditionString(condition, fields);
			case 'Forms\\Entities\\ConditionValue':
				return evaluateConditionValue(condition, fields);
			case 'Forms\\Entities\\ConditionNumeric':
				return evaluateConditionNumeric(condition, fields);
			case 'Forms\\Entities\\ConditionDate':
				return evaluateConditionDate(condition, fields);
			case 'Forms\\Entities\\ConditionMatch':
				return evaluateConditionMatch(condition, fields);
			default:
				return false;
		}
	});
}

/**
 * Process all conditionals' conditions and apply actions if conditions are met
 *
 * @param conditionals
 * @param form
 * @returns {{}}
 */
function processConditionals(conditionals, form) {
	const triggeredActions = {};

	conditionals.forEach((conditional) => {
		if (conditional.conditions.length === 0) {
			return;
		}

		// Evaluate each condition; all must be met for the conditional to be true
		let condMet = processConditions(conditional, form);

		// If all conditions are met, process the conditional's actions
		if (condMet) {
			conditional.actions.forEach((action) => {
				// For field visibility actions, accumulate triggered states
				if (
					action.entity === 'Forms\\Entities\\ActionFieldVisibility' ||
					action.entity === 'Forms\\Entities\\ActionGroupVisibility'
				) {
					const elementId = action.settings.field;

					triggeredActions[elementId] = triggeredActions[elementId] || [];
					triggeredActions[elementId].push(action.settings.state);

					return;
				}

				// For other actions, apply immediately
				applyAction(form, action);
			});

			return;
		}

		// If conditions are not met, revert the actions
		conditional.actions.forEach((action) => revertAction(form, action));
	});

	return triggeredActions;
}

/**
 * Evaluates all conditionals for a given form and applies or reverts actions accordingly.
 *
 * @param {HTMLElement} form - The form element.
 * @param {Array} conditionals - The array of conditional objects.
 * @param formData
 */
function evaluateConditions(form, conditionals, formData) {
	// Process unconditional conditionals
	processUnconditionalConditionals(conditionals, form, formData);

	// Process conditionals that have conditions and return triggered actions
	const triggeredActions = processConditionals(conditionals, form);

	// Determine final visibility state for each target field
	determineFinalVisibility(formData, triggeredActions, form);
}

/**
 * Helper function to get values from fields based on their type
 *
 * @param fields
 *
 * @returns {string[]|*|*[]}
 */
function getFieldValues(fields) {
	if (fields.length === 0) {
		return [];
	}

	const firstField = fields[0];

	if (firstField.type === 'radio') {
		const selected = fields.find((field) => field.checked);

		return selected ? [selected.value] : [];
	} else if (firstField.type === 'checkbox') {
		return fields.filter((field) => field.checked).map((field) => field.value);
	} else if (firstField.tagName === 'SELECT') {
		if (firstField.multiple) {
			return Array.from(firstField.selectedOptions).map((option) => option.value);
		}

		return firstField.value ? [firstField.value] : [];
	}

	return fields.map((field) => field.value).filter((value) => value.trim() !== '');
}

/**
 * Evaluates a string-based condition for a form field.
 * Supports equality, inequality, contains, and not contains checks.
 *
 * @param condition
 * @param fields
 *
 * @returns {boolean} - True if the condition is met, false otherwise.
 */
function evaluateConditionString(condition, fields) {
	const { state, value } = condition.settings;
	const fieldValues = getFieldValues(fields);

	// Compare the field value based on the state
	switch (state) {
		case 'equal':
			return fieldValues.includes(value);
		case 'not_equal':
			return !fieldValues.includes(value);
		case 'contains':
			return fieldValues.some((val) => val.includes(value));
		case 'not_contains':
			return fieldValues.every((val) => !val.includes(value));
		default:
			return false;
	}
}

/**
 * Evaluates a value-based condition for a form field.
 * Checks if the field is filled/checked or empty/unchecked.
 *
 * @param condition
 * @param fields
 *
 * @returns {boolean} - True if the condition is met, false otherwise.
 */
function evaluateConditionValue(condition, fields) {
	const { state } = condition.settings;
	const fieldValues = getFieldValues(fields);
	const isFilled = fieldValues.length > 0;

	// Determine if a field is filled or empty
	switch (state) {
		case 'filled':
		case 'checked':
			return isFilled;
		case 'empty':
		case 'unchecked':
			return !isFilled;
		default:
			return false;
	}
}

/**
 * Evaluates a numeric condition for a form field.
 * Supports less than, greater than, less than or equal, and greater than or equal checks.
 *
 * @param condition
 * @param fields
 *
 * @returns {boolean} - True if the condition is met, false otherwise.
 */
function evaluateConditionNumeric(condition, fields) {
	const { state, value } = condition.settings;
	const condValue = parseFloat(value);

	if (isNaN(condValue)) {
		return false;
	}

	const fieldValues = getFieldValues(fields);

	// Compare numeric values based on the condition
	for (const val of fieldValues) {
		const numVal = parseFloat(val);
		// If parsing fails, return false
		if (!isNaN(numVal)) {
			switch (state) {
				case 'less':
					return numVal < condValue;
				case 'greater':
					return numVal > condValue;
				case 'less_equal':
					return numVal <= condValue;
				case 'greater_equal':
					return numVal >= condValue;
				default:
					break;
			}
		}
	}

	return false;
}

/**
 * Evaluates a date-based condition for a form field.
 * Supports equality, inequality, before, and after checks for dates.
 *
 * @param condition
 * @param fields
 *
 * @returns {boolean} - True if the condition is met, false otherwise.
 */
function evaluateConditionDate(condition, fields) {
	const { state, value } = condition.settings;

	// Parse the field and condition values as dates
	const condDate = new Date(value);

	// If either date is invalid, return false
	if (isNaN(condDate.getTime())) {
		return false;
	}

	const fieldValues = getFieldValues(fields);

	// Compare dates based on the condition
	for (const val of fieldValues) {
		const fieldDate = new Date(val);
		if (!isNaN(fieldDate.getTime())) {
			switch (state) {
				case 'equal':
					return fieldDate.getTime() === condDate.getTime();
				case 'not_equal':
					return fieldDate.getTime() !== condDate.getTime();
				case 'before_date':
					return fieldDate < condDate;
				case 'after_date':
					return fieldDate > condDate;
				default:
					break;
			}
		}
	}

	return false;
}

/**
 * Evaluates a regex-based condition for a form field.
 * Supports matching and not matching a regular expression.
 *
 * @param condition
 * @param fields
 *
 * @returns {boolean} - True if the condition is met, false otherwise.
 */
function evaluateConditionMatch(condition, fields) {
	const { state, value } = condition.settings;

	try {
		let pattern = value;
		let flags = '';

		if (pattern.startsWith('/') && pattern.lastIndexOf('/') > 0) {
			const lastSlashIndex = pattern.lastIndexOf('/');

			pattern = pattern.slice(1, lastSlashIndex);

			flags = pattern.slice(lastSlashIndex + 1);
		}

		const regex = new RegExp(pattern, flags);

		const fieldValues = getFieldValues(fields).filter((val) => val.trim() !== '');

		if (state === 'regex') {
			return fieldValues.some((val) => regex.test(val));
		}
		if (state === 'not_regex') {
			return !fieldValues.some((val) => regex.test(val));
		}
	} catch (e) {
		// Log an error if the regex is invalid
		console.error('Invalid regex:', value, e);
		return false;
	}
	return false;
}

/**
 * Applies a step visibility action to the form.
 * Hides or shows a form step based on the action settings.
 *
 * @param {HTMLElement} form - The form element.
 * @param {Object} action - The action object containing settings.
 * @param {Object} formData - The form's data object.
 */
function applyStepVisibilityAction(form, action, formData) {
	// Get the step element using its data-step-name
	const stepName = action.settings.field;
	const step = form.querySelector(`[data-step-name="${stepName}"]`);

	if (!step) {
		return;
	}

	if (action.settings.state === 'hide') {
		// For each input within the step, record its required state and reset its value
		step.querySelectorAll('input, select, textarea').forEach((input) => {
			const key = input.id || input.name;

			if (!formData.removedRequiredFields.has(key)) {
				formData.removedRequiredFields.set(key, {
					element: input,
					wasRequired: input.hasAttribute('required'),
					source: 'step',
				});
			}

			// Remove 'required' and reset the field
			input.removeAttribute('required');
			resetField(input);
		});

		// Hide the step and mark it as skipped
		step.style.display = 'none';
		step.setAttribute('data-skipped', 'true');

		return;
	}

	// If showing the step, restore its display state
	step.style.display = formData.defaultDisplayStates.get(stepName) || 'block';
	step.removeAttribute('data-skipped');

	// Restore the required attribute for each input if it was originally required
	step.querySelectorAll('input, select, textarea').forEach((input) => {
		const key = input.id || input.name;

		if (formData.removedRequiredFields.has(key) && formData.removedRequiredFields.get(key).source === 'step') {
			const record = formData.removedRequiredFields.get(key);

			if (record.wasRequired) {
				input.setAttribute('required', '');
			}

			// If not overridden by an unconditional default, remove the tracking
			if (!formData.unconditionalDefaults.has(stepName)) {
				formData.removedRequiredFields.delete(key);
			}
		}
	});
}

/**
 * Applies a field visibility action to the form.
 * Hides or shows a form field based on the action settings.
 *
 * @param {HTMLElement} form - The form element.
 * @param {Object} action - The action object containing settings.
 * @param {Object} formData - The form's data object.
 */
function applyFieldVisibilityAction(form, action, formData) {
	const elementId = action.settings.field;
	const targetElement = getTargetElement(form, elementId);

	if (!targetElement) {
		return;
	}

	if (action.settings.state === 'hide') {
		targetElement.style.display = 'none';

		// Handle all inputs within the target element
		targetElement.querySelectorAll('input, select, textarea').forEach((input) => {
			const key = input.id || input.name;

			if (input.hasAttribute('required')) {
				formData.removedRequiredFields.set(key, {
					element: input,
					wasRequired: true,
					source: 'field_visibility',
				});

				input.removeAttribute('required');
			}

			resetField(input);
		});

		return;
	}

	targetElement.style.display = formData.defaultDisplayStates.get(elementId) || '';

	// Restore required attributes for all inputs within the target element
	targetElement.querySelectorAll('input, select, textarea').forEach((input) => {
		const key = input.id || input.name;

		if (
			formData.removedRequiredFields.has(key) &&
			formData.removedRequiredFields.get(key).source === 'field_visibility'
		) {
			const record = formData.removedRequiredFields.get(key);

			if (record.wasRequired) {
				input.setAttribute('required', '');
			}

			formData.removedRequiredFields.delete(key);
		}
	});
}

/**
 * Applies a group visibility action to the form.
 * Hides or shows a form group based on the action settings.
 *
 * @param {HTMLElement} form - The form element.
 * @param {Object} action - The action object containing settings.
 * @param {Object} formData - The form's data object.
 */
function applyGroupVisibilityAction(form, action, formData) {
	// Get the group (fieldset) using its data-id
	const elementId = action.settings.field;
	const group = form.querySelector(`fieldset[data-id="${elementId}"]`);

	if (!group) {
		return;
	}

	if (action.settings.state === 'hide') {
		// For each input in the group, record its required state and reset it
		group.querySelectorAll('input, select, textarea').forEach((input) => {
			const key = input.id || input.name;

			if (!formData.removedRequiredFields.has(key)) {
				formData.removedRequiredFields.set(key, {
					element: input,
					wasRequired: input.hasAttribute('required'),
					source: 'group',
				});
			}

			input.removeAttribute('required');
			resetField(input);
		});

		// Hide the entire group
		group.style.display = 'none';

		return;
	}
	// Restore the group's display state
	group.style.display = formData.defaultDisplayStates.get(elementId) || '';

	// For each input, if it was originally required, restore the attribute
	group.querySelectorAll('input, select, textarea').forEach((input) => {
		const key = input.id || input.name;

		if (formData.removedRequiredFields.has(key) && formData.removedRequiredFields.get(key).source === 'group') {
			const record = formData.removedRequiredFields.get(key);

			if (record.wasRequired) {
				input.setAttribute('required', '');
			}

			// Remove the tracking if not overridden by unconditional defaults
			if (!formData.unconditionalDefaults.has(elementId)) {
				formData.removedRequiredFields.delete(key);
			}
		}
	});
}

/**
 * Applies an update action to the form.
 * Updates the value of specified fields.
 *
 * @param {HTMLElement} form - The form element.
 * @param {Object} action - The action object containing settings.
 * @param {Object} formData - The form's data object.
 */
function applyUpdateAction(form, action, formData) {
	const elementId = action.settings.field;
	const fields = getFieldElements(form, elementId);

	fields.forEach((field) => {
		if (field.tagName === 'INPUT' || field.tagName === 'SELECT' || field.tagName === 'TEXTAREA') {
			if (!formData.originalFieldValues.has(field)) {
				formData.originalFieldValues.set(field, field.value);
			}

			field.value = action.settings.value;

			return;
		}

		const textElement = field.querySelector('.element-text');

		if (!textElement) {
			return;
		}

		if (!formData.originalFieldValues.has(field)) {
			formData.originalFieldValues.set(field, textElement.textContent);
		}

		textElement.textContent = action.settings.value;
	});
}

/**
 * Applies a requirements action to the form.
 * Modifies the required state of specified fields.
 *
 * @param {HTMLElement} form - The form element.
 * @param {Object} action - The action object containing settings.
 * @param {Object} formData - The form's data object.
 */
function applyRequirementsAction(form, action, formData) {
	const elementId = action.settings.field;
	const fields = getFieldElements(form, elementId);

	fields.forEach((field) => {
		const key = field.id || field.name;

		if (action.settings.state === 'not_required' && field.hasAttribute('required')) {
			field.removeAttribute('required');
			formData.removedRequiredFields.set(key, true);
		} else if (action.settings.state === 'required' && !field.hasAttribute('required')) {
			field.setAttribute('required', '');
			formData.addedRequiredFields.set(key, true);
		}
	});
}

/**
 * Applies a class modification action to the form.
 * Adds or removes classes from specified elements.
 *
 * @param {HTMLElement} form - The form element.
 * @param {Object} action - The action object containing settings.
 * @param {Object} formData - The form's data object.
 */
function applyClassAction(form, action, formData) {
	const elementId = action.settings.field;
	const fields = getFieldElements(form, elementId);

	fields.forEach((field) => {
		let block = field;

		if (!field.classList.contains('static-element-block')) {
			block = field.closest('.base-element-block.input-element-block');
		}

		const classes = action.settings.value.split(' ');

		if (!formData.classChanges.has(block)) {
			formData.classChanges.set(block, { added: new Set(), removed: new Set() });
		}

		const changes = formData.classChanges.get(block);

		if (action.settings.state === 'add_class') {
			classes.forEach((className) => {
				block.classList.add(className);
				changes.added.add(className);
			});
		} else if (action.settings.state === 'remove_class') {
			classes.forEach((className) => {
				block.classList.remove(className);
				changes.removed.add(className);
			});
		}
	});
}

/**
 * Applies a given action to the form based on its entity type.
 *
 * @param {HTMLElement} form - The form element.
 * @param {Object} action - The action to be applied.
 */
function applyAction(form, action) {
	const formData = getFormData(form);

	switch (action.entity) {
		case 'Forms\\Entities\\ActionStepVisibility':
			applyStepVisibilityAction(form, action, formData);
			break;
		case 'Forms\\Entities\\ActionFieldVisibility':
			applyFieldVisibilityAction(form, action, formData);
			break;
		case 'Forms\\Entities\\ActionGroupVisibility':
			applyGroupVisibilityAction(form, action, formData);
			break;
		case 'Forms\\Entities\\ActionUpdate':
			applyUpdateAction(form, action, formData);
			break;
		case 'Forms\\Entities\\ActionRequirements':
			applyRequirementsAction(form, action, formData);
			break;
		case 'Forms\\Entities\\ActionClass':
			applyClassAction(form, action, formData);
			break;
		default:
			break;
	}
}

/**
 * Reverts a step visibility action on the form.
 * Restores the step's visibility to its default state.
 *
 * @param {HTMLElement} form - The form element.
 * @param {Object} action - The action object containing settings.
 * @param {Object} formData - The form's data object.
 */
function revertStepVisibilityAction(form, action, formData) {
	// Get the step element
	const stepName = action.settings.field;
	const step = form.querySelector(`[data-step-name="${stepName}"]`);

	if (!step) {
		return;
	}

	// Determine the default state for the step
	const state = formData.unconditionalDefaults.get(stepName) || 'show';
	const newDisplay = state === 'hide' ? 'none' : formData.defaultDisplayStates.get(stepName) || 'block';

	step.style.display = newDisplay;

	// Set or remove the 'data-skipped' attribute based on visibility
	if (newDisplay === 'none') {
		step.setAttribute('data-skipped', 'true');

		// If the step is hidden, ensure required attributes are removed
		step.querySelectorAll('input, select, textarea').forEach((input) => {
			input.removeAttribute('required');
			resetField(input);
		});

		return;
	}

	step.removeAttribute('data-skipped');

	// Restore 'required' attributes for each input if necessary
	step.querySelectorAll('input, select, textarea').forEach((input) => {
		const key = input.id || input.name;

		if (formData.removedRequiredFields.has(key) && formData.removedRequiredFields.get(key).source === 'step') {
			const record = formData.removedRequiredFields.get(key);

			if (record.wasRequired) {
				input.setAttribute('required', '');
			}

			formData.removedRequiredFields.delete(key);
		}
	});
}

/**
 * Reverts a field visibility action on the form.
 * Restores the field's visibility to its default state.
 *
 * @param {HTMLElement} form - The form element.
 * @param {Object} action - The action object containing settings.
 * @param {Object} formData - The form's data object.
 */
function revertFieldVisibilityAction(form, action, formData) {
	const elementId = action.settings.field;
	const targetElement = getTargetElement(form, elementId);

	if (!targetElement) {
		return;
	}

	const state = formData.unconditionalDefaults.get(elementId) || 'show';
	const newDisplay = state === 'hide' ? 'none' : formData.defaultDisplayStates.get(elementId) || '';

	targetElement.style.display = newDisplay;

	if (newDisplay === 'none') {
		// Handle all inputs within the target element when hidden
		targetElement.querySelectorAll('input, select, textarea').forEach((input) => {
			input.removeAttribute('required');
			resetField(input);
		});

		return;
	}

	// Restore required attributes when shown
	targetElement.querySelectorAll('input, select, textarea').forEach((input) => {
		const key = input.id || input.name;

		if (
			formData.removedRequiredFields.has(key) &&
			formData.removedRequiredFields.get(key).source === 'field_visibility'
		) {
			const record = formData.removedRequiredFields.get(key);

			if (record.wasRequired) {
				input.setAttribute('required', '');
			}

			formData.removedRequiredFields.delete(key);
		}
	});
}

/**
 * Reverts a group visibility action on the form.
 * Restores the group's visibility to its default state.
 *
 * @param {HTMLElement} form - The form element.
 * @param {Object} action - The action object containing settings.
 * @param {Object} formData - The form's data object.
 */
function revertGroupVisibilityAction(form, action, formData) {
	const elementId = action.settings.field;
	const group = form.querySelector(`fieldset[data-id="${elementId}"]`);

	if (!group) {
		return;
	}

	// Determine and restore the group's display state
	const state = formData.unconditionalDefaults.get(elementId) || 'show';
	const newDisplay = state === 'hide' ? 'none' : formData.defaultDisplayStates.get(elementId) || '';

	group.style.display = newDisplay;

	// If the group is hidden, remove "required" from all contained fields
	if (newDisplay === 'none') {
		group.querySelectorAll('input, select, textarea').forEach((input) => {
			input.removeAttribute('required');
			resetField(input);
		});

		return;
	}

	// For each input in the group, restore 'required' if it was removed
	group.querySelectorAll('input, select, textarea').forEach((input) => {
		const key = input.id || input.name;

		if (formData.removedRequiredFields.has(key) && formData.removedRequiredFields.get(key).source === 'group') {
			const record = formData.removedRequiredFields.get(key);

			if (record.wasRequired) {
				input.setAttribute('required', '');
			}

			formData.removedRequiredFields.delete(key);
		}
	});
}

/**
 * Reverts a requirements action on the form.
 * Restores the required state of specified fields to their original state.
 *
 * @param {HTMLElement} form - The form element.
 * @param {Object} action - The action object containing settings.
 * @param {Object} formData - The form's data object.
 */
function revertRequirementsAction(form, action, formData) {
	const elementId = action.settings.field;
	const fields = getFieldElements(form, elementId);

	fields.forEach((field) => {
		const key = field.id || field.name;

		if (action.settings.state === 'not_required' && formData.removedRequiredFields.has(key)) {
			field.setAttribute('required', '');
			formData.removedRequiredFields.delete(key);
		} else if (action.settings.state === 'required' && formData.addedRequiredFields.has(key)) {
			field.removeAttribute('required');
			formData.addedRequiredFields.delete(key);
		}
	});
}

/**
 * Reverts a class modification action on the form.
 * Restores the original classes of specified elements.
 *
 * @param {HTMLElement} form - The form element.
 * @param {Object} action - The action object containing settings.
 * @param {Object} formData - The form's data object.
 */
function revertClassAction(form, action, formData) {
	const elementId = action.settings.field;
	const fields = getFieldElements(form, elementId);

	fields.forEach((field) => {
		let block = field;

		if (!field.classList.contains('static-element-block')) {
			block = field.closest('.base-element-block.input-element-block');
		}

		if (!formData.classChanges.has(block)) {
			return;
		}

		const changes = formData.classChanges.get(block);
		const classes = action.settings.value.split(' ');

		if (action.settings.state === 'add_class') {
			classes.forEach((className) => {
				if (changes.added.has(className)) {
					block.classList.remove(className);
					changes.added.delete(className);
				}
			});
		} else if (action.settings.state === 'remove_class') {
			classes.forEach((className) => {
				if (changes.removed.has(className)) {
					block.classList.add(className);
					changes.removed.delete(className);
				}
			});
		}

		if (changes.added.size === 0 && changes.removed.size === 0) {
			formData.classChanges.delete(block);
		}
	});
}

/**
 * Reverts an action previously applied to the form based on its entity type.
 *
 * @param {HTMLElement} form - The form element.
 * @param {Object} action - The action to be reverted.
 */
function revertAction(form, action) {
	const formData = getFormData(form);

	switch (action.entity) {
		case 'Forms\\Entities\\ActionStepVisibility':
			revertStepVisibilityAction(form, action, formData);
			break;
		case 'Forms\\Entities\\ActionFieldVisibility':
			revertFieldVisibilityAction(form, action, formData);
			break;
		case 'Forms\\Entities\\ActionGroupVisibility':
			revertGroupVisibilityAction(form, action, formData);
			break;
		case 'Forms\\Entities\\ActionRequirements':
			revertRequirementsAction(form, action, formData);
			break;
		case 'Forms\\Entities\\ActionClass':
			revertClassAction(form, action, formData);
			break;
		default:
			break;
	}
}
